import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiHome,
  FiUsers,
  FiMap,
  FiDollarSign,
  FiFileText,
  FiMenu,
  FiX,
  FiLogOut,
  FiSettings,
  FiLock,
  FiGlobe,
  FiVideo,
  FiImage,
  FiInfo,
  FiClock,
  FiTruck,
  FiBriefcase,
  FiUserPlus,
  FiMapPin
} from 'react-icons/fi';
import AlterarSenhaModal from './AlterarSenhaModal';

const TITLE_ADMIN = 'NetalimaGest - Gestão de Passageiros';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = TITLE_ADMIN;
  }, [location.pathname]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [senhaModalOpen, setSenhaModalOpen] = useState(false);
  const [submenuAberto, setSubmenuAberto] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: FiHome, label: 'Dashboard', roles: ['administrador', 'suporte', 'financeiro', 'condutor', 'admin', 'atendente'] },
    { path: '/admin/usuarios', icon: FiUserPlus, label: 'Usuários', roles: ['administrador', 'suporte', 'admin'] },
    { path: '/admin/dados-empresa', icon: FiBriefcase, label: 'Informações da Empresa', roles: ['administrador', 'suporte', 'admin'] },
    { path: '/admin/viagens', icon: FiMap, label: 'Viagens', roles: ['administrador', 'suporte', 'condutor', 'admin', 'atendente'] },
    { path: '/admin/passageiros', icon: FiUsers, label: 'Passageiros', roles: ['administrador', 'suporte', 'condutor', 'admin', 'atendente'] },
    { path: '/admin/pre-reservas', icon: FiClock, label: 'Pré-Reservas', roles: ['administrador', 'suporte', 'condutor', 'admin', 'atendente'] },
    { path: '/admin/lista-passageiros', icon: FiFileText, label: 'Lista de Passageiros', roles: ['administrador', 'suporte', 'financeiro', 'condutor', 'admin', 'atendente'] },
    { path: '/admin/contratos-onibus', icon: FiTruck, label: 'Contratos de Ônibus', roles: ['administrador', 'suporte', 'financeiro', 'admin'] },
    { path: '/admin/hospedagens', icon: FiBriefcase, label: 'Hospedagens', roles: ['administrador', 'suporte', 'financeiro', 'admin'], submenu: [
      { path: '/admin/hospedagens/pousadas', icon: FiMapPin, label: 'Pousadas' },
      { path: '/admin/hospedagens/contratos', icon: FiFileText, label: 'Contratos de Hospedagem' },
    ]},
    { path: '/admin/config-contrato', icon: FiFileText, label: 'Config. Contrato Passageiros', roles: ['administrador', 'suporte', 'admin'] },
    { path: '/admin/pagamentos', icon: FiDollarSign, label: 'Pagamentos', roles: ['administrador', 'suporte', 'financeiro', 'admin'] },
    { path: '/admin/site', icon: FiGlobe, label: 'Gerenciar Site', roles: ['administrador', 'suporte', 'admin'], submenu: [
      { path: '/admin/site/quem-somos', icon: FiInfo, label: 'Quem Somos' },
      { path: '/admin/site/galeria-videos', icon: FiVideo, label: 'Galeria de Vídeos' },
      { path: '/admin/site/galeria-fotos', icon: FiImage, label: 'Galeria de Fotos' },
    ]},
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.tipo)
  );

  return (
    <div className="min-h-screen bg-black">
      {/* Sidebar Mobile */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)}></div>
        <div className="fixed inset-y-0 left-0 flex flex-col w-64 bg-gray-800 shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-700">
            <h1 className="text-xl font-bold text-primary-600">NetalimaGest</h1>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-300">
              <FiX size={24} />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.submenu && item.submenu.some(sub => location.pathname === sub.path));
              const temSubmenu = item.submenu && item.submenu.length > 0;
              
              if (temSubmenu) {
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => setSubmenuAberto(submenuAberto === item.path ? null : item.path)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3" size={20} />
                        {item.label}
                      </div>
                      <span className={`transform transition-transform ${submenuAberto === item.path ? 'rotate-90' : ''}`}>
                        ›
                      </span>
                    </button>
                    {submenuAberto === item.path && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setSidebarOpen(false)}
                              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                                isSubActive
                                  ? 'bg-primary-500 text-white'
                                  : 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                              }`}
                            >
                              <SubIcon className="mr-2" size={16} />
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Icon className="mr-3" size={20} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-700">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-300 px-4 py-2">{user?.nome}</p>
              <button
                onClick={() => {
                  setSenhaModalOpen(true);
                  setSidebarOpen(false);
                }}
                className="flex items-center w-full px-4 py-2 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FiLock className="mr-2" size={18} />
                Alterar Senha
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-red-400 rounded-lg hover:bg-red-900/30"
              >
                <FiLogOut className="mr-2" size={18} />
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="flex">
        {/* Sidebar Desktop */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-gray-800 border-r border-gray-700">
          <div className="flex items-center h-16 px-4 border-b border-gray-700">
            <h1 className="text-xl font-bold text-primary-600">NetalimaGest</h1>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.submenu && item.submenu.some(sub => location.pathname === sub.path));
              const temSubmenu = item.submenu && item.submenu.length > 0;
              
              if (temSubmenu) {
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => setSubmenuAberto(submenuAberto === item.path ? null : item.path)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="mr-3" size={20} />
                        {item.label}
                      </div>
                      <span className={`transform transition-transform ${submenuAberto === item.path ? 'rotate-90' : ''}`}>
                        ›
                      </span>
                    </button>
                    {submenuAberto === item.path && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.path;
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                                isSubActive
                                  ? 'bg-primary-500 text-white'
                                  : 'text-gray-400 hover:bg-gray-700 hover:text-gray-300'
                              }`}
                            >
                              <SubIcon className="mr-2" size={16} />
                              {subItem.label}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <Icon className="mr-3" size={20} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-700">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-300 px-4 py-2">{user?.nome}</p>
              <button
                onClick={() => setSenhaModalOpen(true)}
                className="flex items-center w-full px-4 py-2 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <FiLock className="mr-2" size={18} />
                Alterar Senha
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-red-400 rounded-lg hover:bg-red-900/30"
              >
                <FiLogOut className="mr-2" size={18} />
                Sair
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 lg:pl-64 bg-black">
          {/* Top Bar */}
          <header className="sticky top-0 z-30 bg-black border-b border-gray-800 lg:hidden">
            <div className="flex items-center justify-between h-16 px-4">
              <button onClick={() => setSidebarOpen(true)} className="text-gray-300">
                <FiMenu size={24} />
              </button>
              <h1 className="text-lg font-bold text-primary-600">NetalimaGest</h1>
              <div className="w-6"></div>
            </div>
          </header>

          {/* Page Content */}
          <main className="p-4 lg:p-8 bg-black">{children}</main>
        </div>
      </div>

      {/* Modal de Alterar Senha */}
      <AlterarSenhaModal 
        isOpen={senhaModalOpen} 
        onClose={() => setSenhaModalOpen(false)} 
      />
    </div>
  );
};

export default Layout;
